import { useState, useMemo, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  TrendingUp, Plus, DollarSign, MapPin, Calendar, Users, Eye, Edit2, Trash2,
  Briefcase, Award, MessageSquare, AlertCircle, Settings, ArrowUpDown, ArrowUp, ArrowDown
} from 'lucide-react';
import {
  useOportunidades, useFunil, useComercialMutations,
  useEtapasConfig, useContagemEtapas, useSalvarConfig,
  type EtapaFunil,
} from '../../api/hooks/useComercial';
import { Modal } from '../../components/ui/Modal';
import { PageHero } from '../../components/ui/PageHero';
import { DataToolbar, type ViewMode } from '../../components/ui/DataToolbar';
import { KanbanBoard } from '../../components/ui/KanbanBoard';
import { DataPagination } from '../../components/ui/DataPagination';
import { ListManagerDialog, type ListItem } from '../../components/templates/ListManagerDialog';
import { useAuthStore } from '../../store/authStore';
import '../../components/ui/datatable.css';
import { usePersistedState } from '../../hooks/usePersistedState';

/** Helper: gera bg sutil a partir de cor hex */
function hexToBg(hex: string) {
  return hex + '14'; // ~8% opacity
}

export function OportunidadesPage() {
  const { user } = useAuthStore();
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const [busca, setBusca] = useState('');
  const [etapaFilter, setEtapaFilter] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [selectedOp, setSelectedOp] = useState<any | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [viewMode, setViewMode] = usePersistedState<ViewMode>('viewMode', 'lista');
  const [activeColumn, setActiveColumn] = useState<string | null>(null);
  const [showConfig, setShowConfig] = useState(false);
  const [sortField, setSortField] = useState<string>('');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');

  /* ── Fullscreen mode quando Kanban ativo ── */
  useEffect(() => {
    if (viewMode === 'kanban') {
      document.body.classList.add('ac-kanban-fullscreen');
    } else {
      document.body.classList.remove('ac-kanban-fullscreen');
    }
    return () => document.body.classList.remove('ac-kanban-fullscreen');
  }, [viewMode]);

  /** Toggle sort: clica 1x → asc, 2x → desc, 3x → reset */
  const handleSort = useCallback((field: string) => {
    if (sortField === field) {
      if (sortDir === 'asc') {
        setSortDir('desc');
      } else {
        setSortField('');
        setSortDir('asc');
      }
    } else {
      setSortField(field);
      setSortDir('asc');
    }
    setPage(1);
  }, [sortField, sortDir]);

  // ── Etapas dinâmicas ──
  const { data: etapasRaw = [] } = useEtapasConfig();
  const { data: contagemRaw = {} } = useContagemEtapas();
  const salvarConfig = useSalvarConfig();

  const ETAPAS = useMemo(() =>
    etapasRaw
      .sort((a, b) => a.ordem - b.ordem)
      .map(et => ({ ...et, bg: hexToBg(et.color) })),
    [etapasRaw]
  );
  const ETAPAS_VISIVEIS = useMemo(() => ETAPAS.filter(e => e.visivel), [ETAPAS]);

  const scrollToColumn = useCallback((colId: string) => {
    setActiveColumn(colId);
    requestAnimationFrame(() => {
      const col = document.querySelector(`[data-column-id="${colId}"]`);
      if (col) {
        col.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'start' });
        col.classList.add('kb-column-flash');
        setTimeout(() => col.classList.remove('kb-column-flash'), 600);
      }
    });
  }, []);

  const [limite, setLimite] = useState(25);

  // Lista/Cards: dados paginados
  const { data, isLoading, refetch } = useOportunidades({
    page, limite,
    busca: busca || undefined,
    etapaFunil: etapaFilter || undefined,
    orderBy: sortField || undefined,
    orderDir: sortField ? sortDir : undefined,
  });

  // Kanban: todos os dados sem paginação
  const { data: allOps = [], isLoading: isLoadingKanban, refetch: refetchKanban } = useFunil();

  // Filtro local do kanban por busca
  const filteredOps = useMemo(() => {
    if (!busca.trim()) return allOps;
    const q = busca.toLowerCase();
    return allOps.filter((o: any) =>
      o.titulo?.toLowerCase().includes(q) ||
      o.contato?.toLowerCase().includes(q) ||
      o.email?.toLowerCase().includes(q)
    );
  }, [allOps, busca]);

  const { criarOportunidade, atualizarOportunidade, atualizarEtapaOportunidade, excluirOportunidade } = useComercialMutations();

  // ── Handlers ──
  const handleOpenCreate = () => { setSelectedOp(null); setShowModal(true); };

  const handleOpenEdit = (op: any, e: React.MouseEvent) => {
    e.stopPropagation();
    setSelectedOp(op);
    setShowModal(true);
  };

  const handleDelete = async (id: string, e: React.MouseEvent) => {
    e.stopPropagation();
    if (confirm('Tem certeza que deseja excluir esta oportunidade?')) {
      try { await excluirOportunidade.mutateAsync(id); refetch(); refetchKanban(); } catch (err) { console.error(err); }
    }
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsSubmitting(true);
    const fd = new FormData(e.currentTarget);
    const payload = {
      titulo: fd.get('titulo') as string,
      tipo: fd.get('tipo') as string,
      etapaFunil: fd.get('etapaFunil') as string,
      responsavelId: selectedOp?.responsavelId || user?.id || '',
      contato: fd.get('contato') as string || undefined,
      telefone: fd.get('telefone') as string || undefined,
      email: fd.get('email') as string || undefined,
      valorEstimado: Number(fd.get('valorEstimado') || 0),
      dataPrevisao: fd.get('dataPrevisao') as string || undefined,
      observacoes: fd.get('observacoes') as string || undefined,
    };
    try {
      if (selectedOp) await atualizarOportunidade.mutateAsync({ id: selectedOp.id, ...payload });
      else await criarOportunidade.mutateAsync(payload);
      setShowModal(false); refetch(); refetchKanban();
    } catch (err) { console.error(err); }
    finally { setIsSubmitting(false); }
  };

  // ── Stats ──
  const totalPipeline = useMemo(() => {
    const items = viewMode === 'kanban' ? allOps : (data?.items || []);
    return items.reduce((acc: number, op: any) => acc + Number(op.valorEstimado || 0), 0);
  }, [data, allOps, viewMode]);

  const totalRecords = viewMode === 'kanban' ? allOps.length : (data?.total || 0);
  const totalPages = data?.totalPages || 1;
  const ganhasCount = useMemo(() => allOps.filter((op: any) => op.etapaFunil === 'ganho').length, [allOps]);
  const perdidasCount = useMemo(() => allOps.filter((op: any) => op.etapaFunil === 'perdido').length, [allOps]);
  const taxaConversao = ganhasCount + perdidasCount > 0 ? (ganhasCount / (ganhasCount + perdidasCount)) * 100 : 0;

  return (
    <div className="admin-page animate-fadeIn" style={{ marginTop: -22 }}>

      {/* ═══ HERO (oculto no kanban) ═══ */}
      {viewMode !== 'kanban' && (
        <PageHero
          icon={TrendingUp}
          category="Comercial"
          title="Oportunidades Comerciais"
          subtitle={'Gestão detalhada de leads, valores e previsão de encerramento'}
          gradient="indigo"
          inlineStats
          action={{ label: 'Nova Oportunidade', icon: Plus, onClick: handleOpenCreate }}
          stats={[
            { icon: Briefcase, value: totalRecords, label: 'Oportunidades', color: '#c7d2fe' },
            { icon: DollarSign, value: new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL', maximumFractionDigits: 0 }).format(totalPipeline), label: 'Pipeline Total', color: '#34d399' },
            { icon: Award, value: `${taxaConversao.toFixed(1)}%`, label: 'Taxa Conversão', color: '#fbbf24' },
            { icon: Users, value: `${allOps.filter((x: any) => x.etapaFunil !== 'ganho' && x.etapaFunil !== 'perdido').length} ativas`, label: 'Em Andamento', color: '#f472b6' },
          ]}
        />
      )}

      {/* ═══ KANBAN ═══ */}
      {viewMode === 'kanban' ? (
        <div className="data-table-wrapper">
          <DataToolbar
            busca={busca}
            onBuscaChange={(v) => { setBusca(v); setPage(1); }}
            placeholder="Buscar por título ou contato..."
            viewMode={viewMode}
            onViewModeChange={setViewMode}
            viewModes={['lista', 'cards', 'kanban']}
            centerFilters
            filters={
              <div className="kb-nav-strip">
                {/* ── Group: active circles + pipeline bar ── */}
                <div className="kb-nav-pipeline-group">
                  <div className="kb-nav-circles-row">
                    {ETAPAS_VISIVEIS.map((et, idx) => {
                      if (['ganho', 'perdido'].includes(et.id)) return null;
                      const count = filteredOps.filter((o: any) => o.etapaFunil === et.id).length;
                      const isEmpty = count === 0;
                      const isSelected = activeColumn === et.id;
                      return (
                        <button
                          key={et.id}
                          className={`kb-nav-item${isSelected ? ' kb-nav-active' : ''}${isEmpty ? ' kb-nav-empty' : ''}`}
                          title={`${et.label} (${count})`}
                          onClick={() => scrollToColumn(et.id)}
                          style={{ '--kb-circle-color': et.color } as React.CSSProperties}
                        >
                          <span className="kb-nav-circle" style={{ background: et.color }}>{idx + 1}</span>
                          <span className="kb-nav-label">{et.label}</span>
                          {count > 0 && <span className="kb-nav-badge" style={{ background: et.color }}>{count}</span>}
                        </button>
                      );
                    })}
                  </div>
                  {/* ═══ PIPELINE BAR ═══ */}
                  {(() => {
                    const total = filteredOps.length;
                    if (total === 0) return null;
                    const TERMINAIS = ['ganho', 'perdido'];
                    const segments = ETAPAS_VISIVEIS
                      .filter(et => !TERMINAIS.includes(et.id))
                      .map(et => ({
                        id: et.id,
                        label: et.label,
                        color: et.color,
                        count: filteredOps.filter((o: any) => o.etapaFunil === et.id).length,
                      })).filter(s => s.count > 0);
                    const ganhoCount = filteredOps.filter((o: any) => o.etapaFunil === 'ganho').length;
                    const ganhoEtapa = ETAPAS_VISIVEIS.find(e => e.id === 'ganho');
                    const pctGanho = total > 0 ? ((ganhoCount / total) * 100).toFixed(1) : '0.0';
                    const activeTotal = segments.reduce((sum, s) => sum + s.count, 0);
                    if (activeTotal === 0) return null;
                    return (
                      <div className="kb-pipeline-bar">
                        <div className="kb-pipeline-track">
                          {segments.map(seg => {
                            const pct = ((seg.count / activeTotal) * 100);
                            return (
                              <div
                                key={seg.id}
                                className="kb-pipeline-seg"
                                style={{
                                  width: `${pct}%`,
                                  background: seg.color,
                                }}
                                title={`${seg.label}: ${seg.count} (${pct.toFixed(1)}%)`}
                                onClick={() => scrollToColumn(seg.id)}
                              >
                                {pct >= 5 && <span className="kb-pipeline-seg-pct">{pct.toFixed(0)}%</span>}
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    );
                  })()}
                </div>

                {/* ── Terminal etapas (Ganho, Perdido) ── */}
                {ETAPAS_VISIVEIS.filter(et => ['ganho', 'perdido'].includes(et.id)).map((et, _, arr) => {
                  const idx = ETAPAS_VISIVEIS.findIndex(e => e.id === et.id);
                  const count = filteredOps.filter((o: any) => o.etapaFunil === et.id).length;
                  const isEmpty = count === 0;
                  const isSelected = activeColumn === et.id;
                  return (
                    <button
                      key={et.id}
                      className={`kb-nav-item${isSelected ? ' kb-nav-active' : ''}${isEmpty ? ' kb-nav-empty' : ''}`}
                      title={`${et.label} (${count})`}
                      onClick={() => scrollToColumn(et.id)}
                      style={{ '--kb-circle-color': et.color } as React.CSSProperties}
                    >
                      <span className="kb-nav-circle" style={{ background: et.color }}>{idx + 1}</span>
                      <span className="kb-nav-label">{et.label}</span>
                      {count > 0 && <span className="kb-nav-badge" style={{ background: et.color }}>{count}</span>}
                    </button>
                  );
                })}
                <div className="kb-toolbar-separator" />
                <button className="kb-nav-item" onClick={() => setShowConfig(true)} title="Configurar etapas">
                  <span className="kb-nav-circle kb-nav-circle-config"><Settings size={14} /></span>
                  <span className="kb-nav-label">Custom</span>
                </button>
              </div>
            }
          />

          <KanbanBoard
            columns={ETAPAS_VISIVEIS}
            items={filteredOps as any[]}
            getColumnId={(item) => item.etapaFunil}
            hideNav
            activeColumn={activeColumn}
            onDrop={async (itemId, columnId) => {
              await atualizarEtapaOportunidade.mutateAsync({ id: itemId, etapaFunil: columnId });
              refetch(); refetchKanban();
            }}
            onCardClick={(item) => navigate(`/comercial/oportunidades/${item.id}`)}
            isLoading={isLoadingKanban}
            loadingText="Carregando pipeline..."
            renderCard={(op: any, column) => (
              <>
                <div className="kb-card-title">{op.titulo}</div>
                <div className="kb-card-meta">
                  {op.valorEstimado && (
                    <div className="kb-card-meta-value"><DollarSign size={11} />
                      {new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(Number(op.valorEstimado))}
                    </div>
                  )}
                  {(op.municipio || op.estado) && (
                    <div className="kb-card-meta-item"><MapPin size={11} /> {op.municipio?.nome || ''} {op.estado ? `/ ${op.estado.sigla}` : ''}</div>
                  )}
                  {op.contato && <div className="kb-card-meta-item"><Users size={11} /> {op.contato}</div>}
                  {op.dataPrevisao && <div className="kb-card-meta-item"><Calendar size={11} /> Previsão: {new Date(op.dataPrevisao).toLocaleDateString('pt-BR')}</div>}
                  {op._count?.interacoes > 0 && <div className="kb-card-meta-item kb-card-meta-divider"><MessageSquare size={11} /> {op._count.interacoes} interações</div>}
                </div>
              </>
            )}
          />
        </div>
      ) : (
        /* ═══ LISTA / CARDS ═══ */
        <div className="data-table-wrapper" style={{ flex: 1, minHeight: 0 }}>
          <DataToolbar
            busca={busca}
            onBuscaChange={(v) => { setBusca(v); setPage(1); }}
            placeholder="Buscar por título ou contato..."
            totalRecords={totalRecords}
            viewMode={viewMode}
            onViewModeChange={setViewMode}
            viewModes={['lista', 'cards', 'kanban']}
            filters={
              <select value={etapaFilter} onChange={(e) => { setEtapaFilter(e.target.value); setPage(1); }} className="form-select">
                <option value="">Todas as etapas</option>
                {ETAPAS_VISIVEIS.map(et => <option key={et.id} value={et.id}>{et.label}</option>)}
              </select>
            }
          />

          <div style={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
            <div className="dt-container custom-scroll">
              {isLoading ? (
                <div className="dt-loading">Carregando...</div>
              ) : !data?.items?.length ? (
                <div className="dt-empty">Nenhuma oportunidade comercial encontrada.</div>
              ) : viewMode === 'lista' ? (
                <table className="dt-table">
                  <thead>
                    <tr>
                      {[
                        { key: 'titulo', label: 'Título' },
                        { key: 'tipo', label: 'Tipo' },
                        { key: 'etapaFunil', label: 'Etapa' },
                        { key: '', label: 'Responsável' },
                        { key: 'valorEstimado', label: 'Valor Estimado' },
                        { key: 'contato', label: 'Contato' },
                        { key: 'dataPrevisao', label: 'Previsão' },
                      ].map(col => (
                        <th
                          key={col.label}
                          onClick={col.key ? () => handleSort(col.key) : undefined}
                          style={{
                            cursor: col.key ? 'pointer' : 'default',
                            userSelect: 'none',
                            whiteSpace: 'nowrap',
                          }}
                          className={sortField === col.key ? 'dt-th-sorted' : ''}
                        >
                          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                            {col.label}
                            {col.key && (
                              sortField === col.key
                                ? sortDir === 'asc'
                                  ? <ArrowUp size={13} style={{ opacity: 1, color: 'var(--accent-color, #6366f1)' }} />
                                  : <ArrowDown size={13} style={{ opacity: 1, color: 'var(--accent-color, #6366f1)' }} />
                                : <ArrowUpDown size={12} style={{ opacity: 0.25 }} />
                            )}
                          </span>
                        </th>
                      ))}
                      <th className="dt-sticky-right" style={{ textAlign: 'right' }}>Ações</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.items.map((op: any) => {
                      const et = ETAPAS.find(e => e.id === op.etapaFunil) || ETAPAS[0] || { id: '', label: '-', color: '#94a3b8', bg: '#f1f5f9' };
                      return (
                        <tr key={op.id} className="dt-row" style={{ cursor: 'pointer' }} onClick={() => navigate(`/comercial/oportunidades/${op.id}`)}>
                          <td style={{ fontWeight: 700, maxWidth: 220, whiteSpace: 'normal', wordBreak: 'break-word' }}>
                            {op.titulo}
                            {(op.municipio || op.estado) && (
                              <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)', fontWeight: 400, marginTop: 2, display: 'flex', alignItems: 'center', gap: 2 }}>
                                <MapPin size={11} /> {op.municipio?.nome || ''} {op.estado ? `/ ${op.estado.sigla}` : ''}
                              </div>
                            )}
                          </td>
                          <td style={{ textTransform: 'capitalize' }}>{op.tipo.replace('_', ' ')}</td>
                          <td><span className="badge" style={{ background: et.bg, color: et.color }}>{et.label}</span></td>
                          <td>{op.responsavel?.nome || '-'}</td>
                          <td style={{ fontWeight: 700 }}>{op.valorEstimado ? new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(Number(op.valorEstimado)) : '-'}</td>
                          <td style={{ maxWidth: 180, whiteSpace: 'normal', wordBreak: 'break-word' }}>
                            <div>{op.contato || '-'}</div>
                            {op.email && <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>{op.email}</div>}
                          </td>
                          <td>{op.dataPrevisao ? new Date(op.dataPrevisao).toLocaleDateString('pt-BR') : '-'}</td>
                          <td className="dt-sticky-right" style={{ textAlign: 'right' }}>
                            <div style={{ display: 'inline-flex', gap: 4 }} onClick={e => e.stopPropagation()}>
                              <button className="action-btn" onClick={() => navigate(`/comercial/oportunidades/${op.id}`)} title="Ver"><Eye size={15} /></button>
                              <button className="action-btn" onClick={(e) => handleOpenEdit(op, e)} title="Editar" style={{ color: '#f59e0b' }}><Edit2 size={15} /></button>
                              <button className="action-btn action-btn-danger" onClick={(e) => handleDelete(op.id, e)} title="Excluir"><Trash2 size={15} /></button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              ) : (
                /* Cards view */
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: 12, padding: 12 }}>
                  {data.items.map((op: any) => {
                    const et = ETAPAS.find(e => e.id === op.etapaFunil) || ETAPAS[0] || { id: '', label: '-', color: '#94a3b8', bg: '#f1f5f9' };
                    return (
                      <div key={op.id} className="kb-card" onClick={() => navigate(`/comercial/oportunidades/${op.id}`)} style={{ cursor: 'pointer' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8 }}>
                          <div style={{ minWidth: 0 }}>
                            <div className="kb-card-title" style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{op.titulo}</div>
                            <div className="kb-card-meta-item" style={{ marginTop: 2 }}><MapPin size={12} /> {op.municipio?.nome || ''} {op.estado ? `/ ${op.estado.sigla}` : ''}</div>
                          </div>
                          <span className="badge" style={{ background: et.bg, color: et.color, flexShrink: 0 }}>{et.label}</span>
                        </div>
                        <div className="kb-card-meta-divider" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 4 }}>
                          <span className="kb-card-meta-value">{op.valorEstimado ? new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(Number(op.valorEstimado)) : '-'}</span>
                          <div style={{ display: 'flex', gap: 4 }} onClick={e => e.stopPropagation()}>
                            <button className="action-btn" onClick={(e) => handleOpenEdit(op, e)} title="Editar" style={{ color: '#f59e0b' }}><Edit2 size={14} /></button>
                            <button className="action-btn action-btn-danger" onClick={(e) => handleDelete(op.id, e)} title="Excluir"><Trash2 size={14} /></button>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
            <DataPagination
              page={page}
              totalPages={totalPages}
              total={data?.total}
              limite={limite}
              onPageChange={setPage}
              onLimiteChange={setLimite}
              showLimiteSelector
              showPageDropdown
              showFirstLast
            />
          </div>
        </div>
      )}

      {/* ═══ MODAL CRIAR/EDITAR ═══ */}
      <Modal
        open={showModal}
        onClose={() => setShowModal(false)}
        title=""
        size="lg"
        contentClassName="umu-wizard-modal"
        footer={
          <div style={{ display: 'flex', gap: 10, width: '100%', justifyContent: 'flex-end' }}>
            <button type="button" onClick={() => setShowModal(false)} className="btn-cancel">Cancelar</button>
            <button type="submit" form="op-form" disabled={isSubmitting} className="btn-save">
              {isSubmitting ? 'Salvando...' : (selectedOp ? 'Salvar Alterações' : 'Criar Oportunidade')}
            </button>
          </div>
        }
      >
        <form id="op-form" onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
          <div style={{
            background: 'linear-gradient(135deg, #312e81 0%, #3730a3 40%, #4f46e5 100%)',
            borderRadius: '12px 12px 0 0', padding: '20px 24px 16px', position: 'relative', overflow: 'hidden',
          }}>
            <div style={{ position: 'absolute', top: -40, right: -40, width: 140, height: 140, borderRadius: '50%', background: 'rgba(255,255,255,.05)' }} />
            <div style={{ position: 'relative', zIndex: 1 }}>
              <h2 style={{ color: 'white', fontSize: '1.1rem', fontWeight: 800, margin: '0 0 4px' }}>
                {selectedOp ? 'Editar Oportunidade' : 'Nova Oportunidade'}
              </h2>
              <p style={{ color: 'rgba(255,255,255,.55)', fontSize: '.78rem', margin: 0 }}>
                {selectedOp ? 'Atualize as informações desta oportunidade.' : 'Cadastre um novo lead no funil.'}
              </p>
            </div>
          </div>
          <div style={{ padding: '20px 24px', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px 16px', flex: '1 1 auto', minHeight: 0, maxHeight: 'min(580px, 72vh)', overflowY: 'auto' }}>
            <div className="form-group" style={{ gridColumn: 'span 2' }}>
              <label className="form-label">Título *</label>
              <input name="titulo" defaultValue={selectedOp?.titulo || ''} required className="form-input" placeholder="Ex: Prefeitura de Bambuí" />
            </div>
            <div className="form-group">
              <label className="form-label">Tipo *</label>
              <select name="tipo" defaultValue={selectedOp?.tipo || 'prefeitura'} className="form-select">
                <option value="prefeitura">Prefeitura</option>
                <option value="estado">Governo Estadual</option>
                <option value="rede_privada">Rede Privada</option>
                <option value="parceiro">Parceiro Comercial</option>
              </select>
            </div>
            <div className="form-group">
              <label className="form-label">Etapa *</label>
              <select name="etapaFunil" defaultValue={selectedOp?.etapaFunil || 'prospeccao'} className="form-select">
                {ETAPAS_VISIVEIS.map(est => <option key={est.id} value={est.id}>{est.label}</option>)}
              </select>
            </div>
            <div className="form-group">
              <label className="form-label">Nome Contato</label>
              <input name="contato" defaultValue={selectedOp?.contato || ''} className="form-input" placeholder="Nome do lead" />
            </div>
            <div className="form-group">
              <label className="form-label">Telefone</label>
              <input name="telefone" defaultValue={selectedOp?.telefone || ''} className="form-input" placeholder="(11) 99999-9999" />
            </div>
            <div className="form-group">
              <label className="form-label">E-mail</label>
              <input name="email" type="email" defaultValue={selectedOp?.email || ''} className="form-input" placeholder="email@exemplo.com" />
            </div>
            <div className="form-group">
              <label className="form-label">Valor Estimado (R$)</label>
              <input name="valorEstimado" type="number" step="0.01" defaultValue={selectedOp?.valorEstimado || ''} className="form-input" placeholder="0.00" />
            </div>
            <div className="form-group">
              <label className="form-label">Previsão Fechamento</label>
              <input name="dataPrevisao" type="date" defaultValue={selectedOp?.dataPrevisao ? selectedOp.dataPrevisao.split('T')[0] : ''} className="form-input" />
            </div>
            <div></div>
            <div className="form-group" style={{ gridColumn: 'span 2' }}>
              <label className="form-label">Observações</label>
              <textarea name="observacoes" defaultValue={selectedOp?.observacoes || ''} rows={2} className="form-input" style={{ resize: 'vertical' }} placeholder="Histórico ou anotações..." />
            </div>
          </div>
        </form>
      </Modal>

      {/* ═══ CONFIG DIALOG ═══ */}
      <ListManagerDialog
        open={showConfig}
        onClose={() => setShowConfig(false)}
        title="Etapas do Funil"
        items={ETAPAS as ListItem[]}
        onSave={async (updated) => {
          await salvarConfig.mutateAsync(updated as EtapaFunil[]);
          setShowConfig(false);
        }}
        showColor
        showVisibility
        linkedCounts={contagemRaw}
        nameLabel="Nome da Etapa"
        valueLabel="ID (valor)"
        saving={salvarConfig.isPending}
      />
    </div>
  );
}
